/**
 * Display Image Upload Overlay — Vanilla JS
 *
 * Provides drag/zoom/clip-path image compositing on standard Shopify Liquid
 * product pages. Watches for BOLD-injected file inputs, overlays the uploaded
 * image on the first product photo, and writes a composite PNG dataURI into a
 * hidden form field so it reaches the cart and admin.
 *
 * Reuses clip-path config from the React builder's displayUploadConfig.
 *
 * @author Matt Haynes
 * @package Minifigs
 */

import { getDisplayUploadConfig, clipPathToCSS, applyClipPathToCanvas } from '../builder/utils/displayUploadConfig';

// ── State ────────────────────────────────────────────────────────────────

let position = { x: 0, y: 0, scale: 1 };
let dataUri = null;
let isDragging = false;
let dragStart = { x: 0, y: 0 };
let config = null;
let baseImageSrc = '';
let compositeTimeout = null;

// DOM refs (assigned during init)
let overlayContainer = null;
let draggableEl = null;
let uploadedImg = null;
let compositeCanvas = null;
let compositeInput = null;
let controlsContainer = null;
let scaleInput = null;
let scaleLabel = null;
let productMediaEl = null; // the .product__media wrapper (for size calculations)

// ── Helpers ──────────────────────────────────────────────────────────────

function dataURItoBlob(dataURI) {
    const binary = atob(dataURI.split(',')[1]);
    const array = [];
    for (let i = 0; i < binary.length; i++) {
        array.push(binary.charCodeAt(i));
    }
    return new Blob([new Uint8Array(array)], { type: 'image/png' });
}

function updateTransform() {
    if (!draggableEl) return;
    draggableEl.style.transform = `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(${position.scale})`;
}

function updateScaleUI() {
    if (scaleInput) scaleInput.value = position.scale;
    if (scaleLabel) scaleLabel.textContent = `${(position.scale * 100).toFixed(0)}%`;
}

function resetPosition() {
    position = { x: 0, y: 0, scale: 1 };
    updateTransform();
    updateScaleUI();
    generateComposite();
}

// ── Composite generation (debounced) ─────────────────────────────────────

function generateComposite() {
    if (!dataUri || !baseImageSrc || !compositeCanvas || !config) return;

    if (compositeTimeout) clearTimeout(compositeTimeout);

    compositeTimeout = setTimeout(() => {
        const ctx = compositeCanvas.getContext('2d');
        const canvasSize = config.canvasSize || 480;
        compositeCanvas.width = canvasSize;
        compositeCanvas.height = canvasSize;

        const elementWidth = productMediaEl ? productMediaEl.offsetWidth : canvasSize;
        const elementToCanvas = canvasSize / elementWidth;

        const baseImg = new Image();
        baseImg.crossOrigin = 'anonymous';
        baseImg.onload = () => {
            ctx.clearRect(0, 0, canvasSize, canvasSize);
            ctx.drawImage(baseImg, 0, 0, canvasSize, canvasSize);

            const userImg = new Image();
            userImg.onload = () => {
                ctx.save();
                applyClipPathToCanvas(ctx, config.clipPath, canvasSize);

                const cssScaleFactor = Math.min(elementWidth / userImg.width, 1);
                const baseWidth = userImg.width * cssScaleFactor * elementToCanvas;
                const baseHeight = userImg.height * cssScaleFactor * elementToCanvas;
                const finalWidth = baseWidth * position.scale;
                const finalHeight = baseHeight * position.scale;

                const centerX = canvasSize / 2 + position.x * elementToCanvas;
                const centerY = canvasSize / 2 + position.y * elementToCanvas;

                ctx.drawImage(userImg, centerX - finalWidth / 2, centerY - finalHeight / 2, finalWidth, finalHeight);
                ctx.restore();

                try {
                    const compositeDataUri = compositeCanvas.toDataURL('image/png');
                    if (compositeInput) {
                        compositeInput.value = compositeDataUri;
                    }
                } catch (err) {
                    console.error('[DisplayOverlay] Failed to generate composite:', err);
                }
            };
            userImg.src = dataUri;
        };
        baseImg.src = baseImageSrc;
    }, 300);
}

// ── Drag handlers ────────────────────────────────────────────────────────

function onMouseDown(e) {
    e.preventDefault();
    e.stopPropagation();
    isDragging = true;
    dragStart = { x: e.clientX - position.x, y: e.clientY - position.y };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
}

function onTouchStart(e) {
    e.preventDefault();
    e.stopPropagation();
    isDragging = true;
    const touch = e.touches[0];
    dragStart = { x: touch.clientX - position.x, y: touch.clientY - position.y };
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd);
}

function onMouseMove(e) {
    if (!isDragging) return;
    e.preventDefault();
    position.x = e.clientX - dragStart.x;
    position.y = e.clientY - dragStart.y;
    updateTransform();
}

function onTouchMove(e) {
    if (!isDragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    position.x = touch.clientX - dragStart.x;
    position.y = touch.clientY - dragStart.y;
    updateTransform();
}

function onMouseUp() {
    isDragging = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    generateComposite();
}

function onTouchEnd() {
    isDragging = false;
    document.removeEventListener('touchmove', onTouchMove);
    document.removeEventListener('touchend', onTouchEnd);
    generateComposite();
}

// ── Build overlay DOM ────────────────────────────────────────────────────

function buildOverlayDOM() {
    // Overlay container (placed inside .product__media on the first slide)
    overlayContainer = document.getElementById('displayImageOverlay');
    if (!overlayContainer) return false;

    // Controls container (placed after media gallery)
    controlsContainer = document.getElementById('displayUploadControls');

    // Hidden form input
    compositeInput = document.getElementById('displayCompositePreview');

    // Create hidden canvas
    compositeCanvas = document.createElement('canvas');
    compositeCanvas.style.display = 'none';
    document.body.appendChild(compositeCanvas);

    // Intercept form submission to convert base64 dataURI → File blob.
    // product-form.js calls `new FormData(this.form)` which reads form inputs.
    // By converting the hidden input to a file input with a real File object
    // before that happens, FormData will send it as an uploaded file (which
    // Shopify stores as a CDN URL) instead of a raw base64 string.
    const form = compositeInput && compositeInput.closest('form');
    if (form) {
        form.addEventListener(
            'submit',
            () => {
                if (!compositeInput || !compositeInput.value || !compositeInput.value.startsWith('data:')) return;

                try {
                    const blob = dataURItoBlob(compositeInput.value);
                    const file = new File([blob], 'display-composite.png', {
                        type: 'image/png',
                        lastModified: Date.now(),
                    });

                    // Replace hidden input with a file input containing the blob
                    const fileInput = document.createElement('input');
                    fileInput.type = 'file';
                    fileInput.name = compositeInput.name;
                    fileInput.style.display = 'none';

                    // Use DataTransfer to programmatically set the file
                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(file);
                    fileInput.files = dataTransfer.files;

                    compositeInput.parentNode.insertBefore(fileInput, compositeInput);
                    compositeInput.removeAttribute('name'); // prevent duplicate key
                } catch (err) {
                    console.error('[DisplayOverlay] Failed to convert composite to file:', err);
                }
            },
            { capture: true }
        ); // capture phase — fires before product-form.js's handler
    }

    return true;
}

function disableSwiperTouch() {
    // The Swiper instance is stored as .gallery on the <product-media-gallery> custom element
    const galleryEl = document.querySelector('product-media-gallery');
    if (galleryEl && galleryEl.gallery) {
        galleryEl.gallery.allowTouchMove = false;
        galleryEl.gallery.update();
    }
}

function showOverlay(imageDataUri) {
    dataUri = imageDataUri;

    // Disable Swiper drag so it doesn't intercept overlay interactions
    disableSwiperTouch();

    // Apply clip-path from config
    overlayContainer.style.clipPath = clipPathToCSS(config.clipPath);
    overlayContainer.style.display = '';

    // Clear any previous content
    overlayContainer.innerHTML = '';

    // Create draggable wrapper + image
    draggableEl = document.createElement('div');
    draggableEl.className = 'upload-image-draggable';
    uploadedImg = document.createElement('img');
    uploadedImg.src = dataUri;
    uploadedImg.alt = 'Uploaded content';
    draggableEl.appendChild(uploadedImg);
    overlayContainer.appendChild(draggableEl);

    // Attach drag listeners
    draggableEl.addEventListener('mousedown', onMouseDown);
    draggableEl.addEventListener('touchstart', onTouchStart, { passive: false });

    updateTransform();

    // Build controls
    if (controlsContainer) {
        controlsContainer.style.display = '';
        controlsContainer.innerHTML = '';

        const row = document.createElement('div');
        row.className = 'upload-controls__row';

        const label = document.createElement('label');
        label.setAttribute('for', 'display-image-scale');
        label.textContent = 'Zoom';

        scaleInput = document.createElement('input');
        scaleInput.id = 'display-image-scale';
        scaleInput.type = 'range';
        scaleInput.min = '0.5';
        scaleInput.max = '3';
        scaleInput.step = '0.1';
        scaleInput.value = position.scale;

        scaleLabel = document.createElement('span');
        scaleLabel.textContent = `${(position.scale * 100).toFixed(0)}%`;

        scaleInput.addEventListener('input', (e) => {
            position.scale = parseFloat(e.target.value);
            updateTransform();
            updateScaleUI();
            generateComposite();
        });

        row.appendChild(label);
        row.appendChild(scaleInput);
        row.appendChild(scaleLabel);
        controlsContainer.appendChild(row);

        const resetBtn = document.createElement('button');
        resetBtn.type = 'button';
        resetBtn.className = 'btn btn-pill btn-secondary reset-position';
        resetBtn.textContent = 'Reset zoom & position';
        resetBtn.addEventListener('click', resetPosition);
        controlsContainer.appendChild(resetBtn);
    }

    // Resolve base product image from the first slide
    const firstSlide = document.querySelector('.swiper-product-media-gallery .swiper-slide');
    if (firstSlide) {
        const img = firstSlide.querySelector('.product__media img');
        if (img) {
            baseImageSrc = img.src;
            // Store reference to the media element for size calculations
            productMediaEl = img.closest('.product__media');
        }
    }

    generateComposite();
}

// ── File input detection ─────────────────────────────────────────────────

function handleFileChange(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
        resetPosition();
        showOverlay(evt.target.result);
    };
    reader.readAsDataURL(file);
}

function findAndAttachFileInput(container) {
    if (!config || !config.inputSelectors) return false;

    for (const selector of config.inputSelectors) {
        const input = container.querySelector(selector);
        if (input) {
            input.addEventListener('change', handleFileChange);
            return true;
        }
    }

    // Fallback: bind any file input when product-specific selector names differ.
    const fallbackInput = container.querySelector('input[type="file"]');
    if (fallbackInput) {
        fallbackInput.addEventListener('change', handleFileChange);
        return true;
    }

    return false;
}

// ── Main init ────────────────────────────────────────────────────────────

const displayImageUploadOverlay = () => {
    // Read numeric product ID from the page data attribute we inject in Liquid
    const productDataEl = document.getElementById('displayImageOverlay');
    if (!productDataEl) return;

    const numericId = productDataEl.dataset.productId;
    if (!numericId) return;

    const gid = `gid://shopify/Product/${numericId}`;
    config = getDisplayUploadConfig(gid);
    if (!config) return;

    if (!buildOverlayDOM()) return;

    // Watch for BOLD file input injection
    const boldContainer = document.querySelector('.bold_options');
    if (boldContainer) {
        // Check immediately
        if (!findAndAttachFileInput(boldContainer)) {
            // Watch for dynamic injection
            const observer = new MutationObserver(() => {
                if (findAndAttachFileInput(boldContainer)) {
                    observer.disconnect();
                }
            });
            observer.observe(boldContainer, { childList: true, subtree: true });
        }
    }

    // Also try the whole form container in case BOLD places inputs elsewhere
    const formContainer = document.querySelector('.js-but-bits-product-form');
    if (formContainer && formContainer !== boldContainer) {
        if (!findAndAttachFileInput(formContainer)) {
            const observer = new MutationObserver(() => {
                if (findAndAttachFileInput(formContainer)) {
                    observer.disconnect();
                }
            });
            observer.observe(formContainer, { childList: true, subtree: true });
        }
    }
};

// Auto-init on DOMContentLoaded
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', displayImageUploadOverlay);
} else {
    displayImageUploadOverlay();
}

export default displayImageUploadOverlay;
